<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <confirmation-dialog
      v-if="customHoursConfirmation"
      :visible="customHoursConfirmation"
      :message="$t('message.confirm_overwrite_hours')"
      :confirm-title="$t('button.save')"
      :cancel-title="$t('button.cancel')"
      @confirm="save"
      @close="customHoursConfirmation = false"
    />
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        s
        :disabled="!isChanged"
        @click="submit"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <v-row justify="center">
        <v-col
          cols="12"
          md="8"
        >
          <v-row>
            <settings-button-block
              :title="$t('title.simple_opening_hours')"
              :description="$t('label.description_simple_hours')"
            >
              <v-btn
                :block="$vuetify.breakpoint.xsOnly"
                color="primary"
                outlined
                @click="$router.push('/settings/hours')"
              >
                {{ $t('button.switch_to_advanced_hours') }}
              </v-btn>
            </settings-button-block>
            <row-form-divider />
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-select
                v-model="bookingInterval"
                class="pb-0"
                :items="bookingIntervals"
                :label="$t('label.booking_interval')"
                @input="bookingIntervalChanged"
              />
            </v-col>

            <!-- Grid -->
            <v-col cols="12">
              <template v-for="(day, i) in days">
                <v-row
                  :key="`day_${i}`"
                  class="row py-2 d-flex my-2"
                >
                  <v-col class="pa-0 d-flex align-center flex-grow-0 pl-5">
                    <v-checkbox
                      :input-value="open[day.value]"
                      class="pa-0 ma-0"
                      hide-details
                      @change="(open) => changeOpen(day.value, open)"
                    />
                  </v-col>
                  <v-col
                    style="max-width: 50px"
                    :class="[
                      'py-0 py-0 d-flex align-center',
                      `${open[day.value] ? 'text--primary' : 'text--secondary'}`]"
                  >
                    <span class="font-weight-bold">{{ day.text }}</span>
                  </v-col>
                  <v-col class="py-0 py-0 flex-grow-1">
                    <v-range-slider
                      :value="slots[day.value]"
                      :disabled="!open[day.value]"
                      :min="24"
                      :max="96"
                      :step="slotStep"
                      track-color="grey lighten-1"
                      hide-details
                      @input="(bounds) => changeSlot(day.value, bounds)"
                    />
                  </v-col>
                  <v-col
                    cols="2"
                    :class="[
                      'pa-0 ma-0 d-flex align-center',
                      `${open[day.value] ? 'text--primary' : 'text--secondary'}`]"
                    style="min-width: 120px"
                  >
                    <template v-if="open[day.value]">
                      {{ timeFromSlot(slots[day.value][0]) }} -
                      {{ timeFromSlot(slots[day.value][1]) }}
                    </template>
                    <template v-else>
                      {{ $t('label.closed') }}
                    </template>
                  </v-col>
                </v-row>
              </template>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import UnsavedGuard from '@/mixins/unsaved-guard';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import { performSaveAction } from '@/services/vue-utils';
import { isModelEqualToModel } from '@/model/model-utils';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import { niceTimeTextFromSlot, weekdayNumberFromDateDay } from '@/services/time-utils';
import OpeningHour from '@/model/OpeningHour';
import {
  guessIntervalFromHoursInWeekdays,
  hoursFromSimpleHoursInWeekdays, isOnlySimpleHours, openingHoursToSimpleHoursInWeekdays, roundedSimpleHoursInWeekdays,
  SimpleHour,
} from '@/services/hours-utils';
import SettingsButtonBlock from '../forms/SettingsButtonBlock.vue';

export default Vue.extend({
  components: {
    SettingsForm,
    SettingsButtonBlock,
  },
  mixins: [UnsavedGuard, SettingsBreadcrumbs],
  data() {
    return {
      breadcrumbs: [],
      oldHours: [] as OpeningHour[],
      bookingInterval: 15,
      bookingIntervals: [
        { text: this.$i18n.t('label.15_minutes'), value: 15 },
        { text: this.$i18n.t('label.30_minutes'), value: 30 },
      ],
      simpleHoursInWeekdays: [] as SimpleHour[], // in weekdays - starting from 1 (Mon === 1, Tue === 2, ....)
      unwatch: Function as (() => void),
      customHoursConfirmation: false,
    };
  },
  computed: {
    isChanged(): boolean {
      return !isModelEqualToModel(this.hours, this.oldHours);
    },
    days(): {value: number, text: string}[] {
      return this.$localized.weekdays();
    },
    is30minInterval(): boolean {
      return this.bookingInterval === 30;
    },
    slotStep(): number {
      return this.bookingInterval === 30 ? 2 : 1;
    },
    open(): boolean[] {
      return Array.from({ length: this.days.length }, (x, day) => {
        const weekdayNumber = weekdayNumberFromDateDay(day);
        const sh = this.simpleHoursInWeekdays[weekdayNumber] ?? new SimpleHour();
        return sh.isOpen ?? false;
      });
    },
    slots(): number[][] {
      return Array.from({ length: this.days.length }, (x, day) => {
        const weekdayNumber = weekdayNumberFromDateDay(day);
        const sh = this.simpleHoursInWeekdays[weekdayNumber] ?? new SimpleHour();
        return [sh.beginSlot, sh.endSlot];
      });
    },
    hours(): OpeningHour[] {
      return hoursFromSimpleHoursInWeekdays(this.simpleHoursInWeekdays, this.is30minInterval);
    },
  },
  created() {
    this.reset();
    this.unwatch = this.$tstore.watch((state, getters) => getters.isLoaded, (newValue, oldValue) => { this.reset(); });
  },
  beforeDestroy() {
    this.unwatch();
  },
  destroyed() {},
  mounted() {
    this.reset();
  },
  methods: {
    reset() {
      this.oldHours = this.$tstore.state.settings.openingHours;
      const hourMaps = this.$tstore.getters.openingHoursInWeekdaysByTimeIndex as Map<number, OpeningHour>[];

      const simpleHoursInWeekdays = openingHoursToSimpleHoursInWeekdays(hourMaps, this.is30minInterval);
      this.simpleHoursInWeekdays = simpleHoursInWeekdays;

      const bookingInterval = guessIntervalFromHoursInWeekdays(hourMaps);
      this.bookingInterval = bookingInterval;
    },
    changeSlot(day: number, slots: number[]) {
      const weekdayNumber = weekdayNumberFromDateDay(day);
      const sh = this.simpleHoursInWeekdays[weekdayNumber];
      [sh.beginSlot, sh.endSlot] = [...slots];
    },
    changeOpen(day: number, open: boolean) {
      const weekdayNumber = weekdayNumberFromDateDay(day);
      const sh = this.simpleHoursInWeekdays[weekdayNumber];
      sh.isOpen = open;
    },
    bookingIntervalChanged(interval: number) {
      if (!this.is30minInterval) return;
      this.simpleHoursInWeekdays = roundedSimpleHoursInWeekdays(this.simpleHoursInWeekdays);
    },
    async submit() {
      const isSimple = isOnlySimpleHours(this.$tstore.getters.openingHoursInWeekdaysByTimeIndex);
      if (isSimple) this.save();
      else this.customHoursConfirmation = true;
    },

    async save() {
      const entities = this.hours;

      const ok = await performSaveAction(
        undefined,
        async () => this.$tstore.dispatch('sendOpeningHours', { entities }),
      );

      if (ok) {
        this.reset();
      }
    },
    timeFromSlot(slot: number): string {
      return this.$localized.hourMinTextFrom24hr(niceTimeTextFromSlot(slot)) || '';
    },
  },
});
</script>

<style scoped>

  .openinghours-slot-button {
    color: white !important;
  }

</style>
